import React, { useState } from "react";
import { useTranslation } from "@/i18n/LanguageContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import StarRating from "@/components/StarRating";

interface RatingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: number, comment: string) => void;
  isSubmitting: boolean;
  initialRating?: number;
}

export function RatingDialog({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isSubmitting, 
  initialRating = 0 
}: RatingDialogProps) {
  const { t } = useTranslation();
  const [ratingValue, setRatingValue] = useState<number>(initialRating);
  const [ratingComment, setRatingComment] = useState("");

  const handleSubmit = () => {
    if (ratingValue === 0) return;
    onSubmit(ratingValue, ratingComment);
  };

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("desafios_cacatais.rating_dialog.title", { fallback: "Rate this puzzle" })}</DialogTitle>
          <DialogDescription>
            {t("desafios_cacatais.rating_dialog.description", { fallback: "How would you rate this puzzle?" })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          {/* Star Rating */}
          <div className="flex flex-col items-center gap-2">
            <Label htmlFor="rating" className="text-center text-zinc-300">
              {t("desafios_cacatais.rating_dialog.select_rating", { fallback: "Select your rating" })}
            </Label>
            <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
              <StarRating 
                value={ratingValue} 
                onChange={setRatingValue} 
                size={42} 
                color="text-yellow-400" 
              />
            </div>
          </div>
          
          {/* Comment Section */}
          <div className="grid gap-2">
            <Label htmlFor="ratingComment" className="text-zinc-300">
              {t("desafios_cacatais.rating_dialog.comment", { fallback: "Your comment (optional)" })}
            </Label>
            <textarea
              id="ratingComment"
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              placeholder={t("desafios_cacatais.rating_dialog.comment_placeholder", { fallback: "Share your thoughts about this puzzle..." })}
              className="bg-zinc-800 border border-zinc-700 hover:border-zinc-600 rounded-md p-3 min-h-[100px] text-white resize-none focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
            />
          </div>
        </div>
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
            {t("desafios_cacatais.rating_dialog.cancel", { fallback: "Cancel" })}
          </Button>
          <Button 
            type="button" 
            onClick={handleSubmit} 
            disabled={isSubmitting || ratingValue === 0}
            className="w-full sm:w-auto bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("desafios_cacatais.rating_dialog.submitting", { fallback: "Submitting..." })}
              </>
            ) : (
              t("desafios_cacatais.rating_dialog.submit", { fallback: "Submit" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
